import { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { useAuthState } from "@/hooks/useAuth"
import { useUserData } from "@/hooks/useUserData"
import { QUERY_TATTOOS_ENDPOINT } from "@/config/endpoints"
import { db } from "@/lib/firebase"
import { doc, getDoc } from "firebase/firestore"
import type { PortfolioItem } from "@/types/models"
import type { SortingOption } from "@/components/shared/tattoo/filters/SortingFilter"
import { useFeedCache } from "@/contexts/FeedCacheContext"
import {
  createDiscoverTraceId,
  discoverPerfNow,
  finishDiscoverLoadPhase,
  markDiscoverLoadPhase,
} from "@/lib/discoverPerf"

export interface QueryTattoosFilters {
  collections: ("flashes" | "portfolioItems" | "healedPortfolioItems")[]
  sorting?: SortingOption
  cities?: string[]
  artistId?: string
  style?: string
  includeBooked?: boolean
  deals?: boolean
  /** Bumps to re-query same filters without clearing the grid (smooth profile edits / deletes) */
  clientRefetchNonce?: number
  /** Bumps on new uploads; always triggers a full reload so new docs are not merged with a soft replace */
  clientUploadRevision?: number
}

/** Strip client-only fields before sending to the API */
function queryTattoosFiltersForRequest(
  filters: QueryTattoosFilters
): Omit<QueryTattoosFilters, "clientRefetchNonce" | "clientUploadRevision"> {
  const { clientRefetchNonce: _n, clientUploadRevision: _u, ...rest } = filters
  return rest
}

/** Maps UI-only sorts to values accepted by query-tattoos. */
function sortingForQueryTattoosApi(
  filters: Omit<QueryTattoosFilters, "clientRefetchNonce" | "clientUploadRevision">
): SortingOption | undefined {
  if (filters.sorting === "fyp") {
    return "trending"
  }
  return filters.sorting
}

interface TattooItemRef {
  type: "flash" | "portfolioItem" | "healedPortfolioItem"
  id: string
}

interface ApiFeedItem {
  type: "flash" | "portfolioItem" | "healedPortfolioItem"
  id: string
  artistId?: string
  mediaType?: PortfolioItem["type"]
  storagePath?: string
  imageUrl?: string
  compressedImageUrl?: string
  width?: number
  height?: number
  styleTags?: string[]
  caption?: string
  createdAt?: string | null
  available?: boolean
  booked?: boolean
  minPrice?: string
  minSize?: string
  accessCode?: string
  eventId?: string
  eventExclusive?: boolean
  discount?: {
    absoluteAmount?: number
    procentualAccount?: number
    newPrice?: number
    oldPrice?: number
    discountStartsAt?: string
    discountEndsAt?: string
  }
  timedSaves?: Array<{
    itemId: string
    userId: string
    savedAt?: string
  }>
  isFlash?: boolean
  isHealed?: boolean
}

interface QueryTattoosResponse {
  left: TattooItemRef[]
  right: TattooItemRef[]
  items?: ApiFeedItem[]
}

const ITEMS_PER_LOAD = 40

function parseApiDate(value: unknown): Date {
  if (value instanceof Date) {
    return value
  }
  if (typeof value === "string" && value.length > 0) {
    const parsedDate = new Date(value)
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate
    }
  }
  return new Date()
}

function mapApiItemToPortfolioItem(apiItem: ApiFeedItem): PortfolioItem | null {
  if (!apiItem.id || !apiItem.artistId || !apiItem.imageUrl) {
    return null
  }

  const portfolioItem: PortfolioItem = {
    id: apiItem.id,
    artistId: apiItem.artistId,
    type: apiItem.mediaType ?? "image",
    storagePath: apiItem.storagePath ?? "",
    imageUrl: apiItem.imageUrl,
    width: apiItem.width ?? 0,
    height: apiItem.height ?? 0,
    createdAt: parseApiDate(apiItem.createdAt),
  }

  if (apiItem.compressedImageUrl) portfolioItem.compressedImageUrl = apiItem.compressedImageUrl
  if (apiItem.styleTags) portfolioItem.styleTags = apiItem.styleTags
  if (apiItem.caption) portfolioItem.caption = apiItem.caption
  if (apiItem.timedSaves) {
    portfolioItem.timedSaves = apiItem.timedSaves.map((saveData) => ({
      itemId: saveData.itemId,
      userId: saveData.userId,
      savedAt: parseApiDate(saveData.savedAt),
    }))
  }

  if (apiItem.isFlash || apiItem.type === "flash") {
    portfolioItem.isFlash = true
    if (apiItem.minPrice) portfolioItem.minPrice = apiItem.minPrice
    if (apiItem.minSize) portfolioItem.minSize = apiItem.minSize
    if (apiItem.available !== undefined) portfolioItem.available = apiItem.available
    if (apiItem.booked !== undefined) portfolioItem.booked = apiItem.booked
    if (apiItem.accessCode) portfolioItem.accessCode = apiItem.accessCode
    if (apiItem.eventId) portfolioItem.eventId = apiItem.eventId
    if (apiItem.eventExclusive !== undefined) portfolioItem.eventExclusive = apiItem.eventExclusive
    if (apiItem.discount) {
      const { newPrice, oldPrice } = apiItem.discount
      if (newPrice != null && oldPrice != null) {
        portfolioItem.discount = {
          absoluteAmount: apiItem.discount.absoluteAmount,
          procentualAccount: apiItem.discount.procentualAccount,
          newPrice,
          oldPrice,
          discountStartsAt: parseApiDate(apiItem.discount.discountStartsAt),
          discountEndsAt: parseApiDate(apiItem.discount.discountEndsAt),
        }
      }
    }
  }

  if (apiItem.isHealed || apiItem.type === "healedPortfolioItem") {
    portfolioItem.isHealed = true
  }

  return portfolioItem
}

function mapApiItemsToPortfolioItems(
  apiItems: ApiFeedItem[],
  alreadyLoadedItemIds: Set<string>
): PortfolioItem[] {
  const orderedItems: PortfolioItem[] = []
  for (const apiItem of apiItems) {
    const portfolioItem = mapApiItemToPortfolioItem(apiItem)
    if (!portfolioItem || alreadyLoadedItemIds.has(portfolioItem.id)) {
      continue
    }
    orderedItems.push(portfolioItem)
  }
  return orderedItems
}

export interface UseQueryTattoosOptions {
  /** Keep calling loadMore until this id appears in loaded items (if present in the query id lists). */
  prefetchUntilItemId?: string | null
}

/**
 * Hook to query tattoos from the backend using the query_tattoos endpoint
 * Handles fetching document IDs from backend and loading actual documents in batches.
 * When a cacheKey is provided, results are stored in FeedCacheContext so navigating
 * back to the feed shows the previous results instantly.
 */
export function useQueryTattoos(
  filters: QueryTattoosFilters | undefined,
  cacheKey?: string,
  options?: UseQueryTattoosOptions
) {
  const { user, loading: authLoading } = useAuthState()
  const { isAdminModeActive, loading: userDataLoading } = useUserData()
  const feedCache = useFeedCache()

  // Hydrate from cache immediately so the feed renders without a loading skeleton
  const cachedEntry = cacheKey ? feedCache.getEntry(cacheKey) : null

  const [allLeftItems, setAllLeftItems] = useState<TattooItemRef[]>(() => cachedEntry?.allLeftItems ?? [])
  const [allRightItems, setAllRightItems] = useState<TattooItemRef[]>(() => cachedEntry?.allRightItems ?? [])
  const [loadedItems, setLoadedItems] = useState<PortfolioItem[]>(() => cachedEntry?.items ?? [])
  const [loading, setLoading] = useState(!cachedEntry)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [initialLoadComplete, setInitialLoadComplete] = useState(!!cachedEntry)
  const [currentLoadedCount, setCurrentLoadedCount] = useState(() => cachedEntry?.currentLoadedCount ?? 0)
  
  // Track the current request ID to ignore stale results
  const currentRequestIdRef = useState(() => ({ current: 0 }))[0]
  // Track which item IDs we've already loaded to prevent duplicates
  const loadedItemIdsRef = useRef(new Set<string>(cachedEntry?.loadedItemIds ?? []))
  // Track the last cache key we hydrated from so we can reset when it changes
  const lastCacheKeyRef = useRef<string | undefined>(cacheKey)
  const lastCoreFiltersKeyRef = useRef<string | null>(null)
  const lastRefetchNonceRef = useRef<number>(0)
  const lastUploadRevisionRef = useRef<number>(0)

  // Don't query until we know the user's admin status to avoid a wasted request
  const userDataReady = !authLoading && (!user || !userDataLoading)

  // Memoize filters to prevent unnecessary re-fetches
  const memoizedFilters = useMemo(() => {
    return JSON.stringify(filters)
  }, [filters])

  // Fetch IDs from backend when filters change
  useEffect(() => {
    const cacheKeyChanged = lastCacheKeyRef.current !== cacheKey
    lastCacheKeyRef.current = cacheKey

    if (!filters || !userDataReady) {
      if (!filters) {
        markDiscoverLoadPhase("query_tattoos_waiting_filters", { userDataReady })
      } else if (!userDataReady) {
        markDiscoverLoadPhase("query_tattoos_waiting_user_data", {
          authLoading,
          userDataLoading,
        })
      }
      // Keep loading state while waiting for filters or user data
      // But don't reset if we have a valid cache entry — show it while waiting
      const freshCacheEntry = cacheKey ? feedCache.getEntry(cacheKey) : null
      if (!freshCacheEntry) {
        setLoading(true)
        setInitialLoadComplete(false)
        setLoadedItems([])
        setAllLeftItems([])
        setAllRightItems([])
        loadedItemIdsRef.current.clear()
      }
      return
    }

    // If we have a fresh cache entry for this exact key (and key didn't just change), skip the network fetch
    const freshCacheEntry = cacheKey ? feedCache.getEntry(cacheKey) : null
    if (freshCacheEntry && !cacheKeyChanged) {
      markDiscoverLoadPhase("query_tattoos_cache_hit", {
        cacheKey,
        itemCount: freshCacheEntry.items.length,
      })
      // Hydrate state from cache in case this is a re-render after navigation
      setLoadedItems(freshCacheEntry.items)
      setAllLeftItems(freshCacheEntry.allLeftItems)
      setAllRightItems(freshCacheEntry.allRightItems)
      setCurrentLoadedCount(freshCacheEntry.currentLoadedCount)
      loadedItemIdsRef.current = new Set(freshCacheEntry.loadedItemIds)
      setLoading(false)
      setInitialLoadComplete(true)
      // Cache path skips fetchIds(), which normally bumps this ref. Load more and batch
      // stale checks still need a non-zero id so pagination works after restore.
      if (currentRequestIdRef.current === 0) {
        currentRequestIdRef.current = 1
      }
      return
    }

    const coreFiltersKey = filters ? JSON.stringify(queryTattoosFiltersForRequest(filters)) : ""
    const refetchNonce = filters?.clientRefetchNonce ?? 0
    const uploadRevision = filters?.clientUploadRevision ?? 0
    const isFirstFiltersRun = lastCoreFiltersKeyRef.current === null
    const coreFiltersChanged =
      !isFirstFiltersRun && lastCoreFiltersKeyRef.current !== coreFiltersKey
    const uploadRevisionChanged =
      !isFirstFiltersRun && lastUploadRevisionRef.current !== uploadRevision
    const isSoftRefetch =
      !isFirstFiltersRun &&
      !coreFiltersChanged &&
      !uploadRevisionChanged &&
      refetchNonce !== lastRefetchNonceRef.current &&
      refetchNonce > lastRefetchNonceRef.current

    lastCoreFiltersKeyRef.current = coreFiltersKey
    lastRefetchNonceRef.current = refetchNonce
    lastUploadRevisionRef.current = uploadRevision

    const fetchIds = async () => {
      // Increment request ID for this new query
      currentRequestIdRef.current += 1
      const thisRequestId = currentRequestIdRef.current
      const traceId = createDiscoverTraceId()
      const fetchStartedAt = discoverPerfNow()
      
      try {
        markDiscoverLoadPhase("query_tattoos_fetch_start", {
          requestId: thisRequestId,
          traceId,
          cacheKey,
          filters: queryTattoosFiltersForRequest(filters),
        })
        if (!isSoftRefetch) {
          setLoading(true)
          setError(null)
          setLoadedItems([])
          setCurrentLoadedCount(0)
          setInitialLoadComplete(false)
          loadedItemIdsRef.current.clear()
        } else {
          setError(null)
        }

        const filterPayload = queryTattoosFiltersForRequest(filters)
        const requestBody = {
          ...filterPayload,
          sorting: sortingForQueryTattoosApi(filterPayload),
          userId: user?.uid || null,
          isAdmin: isAdminModeActive,
          offset: 0,
          limit: ITEMS_PER_LOAD,
        }

        const apiStartedAt = discoverPerfNow()
        const response = await fetch(QUERY_TATTOOS_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Discover-Trace-Id": traceId,
          },
          body: JSON.stringify(requestBody),
        })
        finishDiscoverLoadPhase("query_tattoos_api", apiStartedAt, {
          requestId: thisRequestId,
          traceId,
          status: response.status,
        })

        if (!response.ok) {
          throw new Error(`Backend returned ${response.status}: ${response.statusText}`)
        }

        const data: QueryTattoosResponse = await response.json()
        
        if (thisRequestId !== currentRequestIdRef.current) {
          return
        }
        
        setAllLeftItems(data.left)
        setAllRightItems(data.right)

        if (data.left.length === 0 && data.right.length === 0) {
          loadedItemIdsRef.current.clear()
          setLoadedItems([])
          setCurrentLoadedCount(0)
        } else if (data.items && data.items.length > 0) {
          if (isSoftRefetch) {
            loadedItemIdsRef.current.clear()
          }
          const hydratedItems = mapApiItemsToPortfolioItems(data.items, loadedItemIdsRef.current)
          hydratedItems.forEach((item) => loadedItemIdsRef.current.add(item.id))
          setLoadedItems((previousItems) => {
            const updatedItems = isSoftRefetch ? hydratedItems : [...previousItems, ...hydratedItems]
            if (cacheKey) {
              feedCache.setEntry(cacheKey, {
                items: updatedItems,
                allLeftItems: data.left,
                allRightItems: data.right,
                loadedItemIds: new Set(loadedItemIdsRef.current),
                currentLoadedCount: updatedItems.length,
                cachedAt: Date.now(),
                scrollPosition: 0,
              })
            }
            return updatedItems
          })
          setCurrentLoadedCount((previousCount) =>
            isSoftRefetch ? hydratedItems.length : previousCount + hydratedItems.length
          )
          finishDiscoverLoadPhase("query_tattoos_hydrated_items", fetchStartedAt, {
            requestId: thisRequestId,
            traceId,
            itemCount: hydratedItems.length,
          })
        } else {
          const batchStartedAt = discoverPerfNow()
          await loadItemsBatch(
            data.left,
            data.right,
            0,
            ITEMS_PER_LOAD,
            thisRequestId,
            isSoftRefetch
          )
          finishDiscoverLoadPhase("query_tattoos_firestore_batch", batchStartedAt, {
            requestId: thisRequestId,
            traceId,
            requestedCount: ITEMS_PER_LOAD,
          })
        }
        
        // Check again before setting complete (in case filters changed during batch load)
        if (thisRequestId === currentRequestIdRef.current) {
          setInitialLoadComplete(true)
          finishDiscoverLoadPhase("query_tattoos_initial_load_complete", fetchStartedAt, {
            requestId: thisRequestId,
            traceId,
            leftCount: data.left.length,
            rightCount: data.right.length,
          })
        }
      } catch (err) {
        console.error("[useQueryTattoos] fetch failed:", err)
        // Only set error if this is still the current request
        if (thisRequestId === currentRequestIdRef.current) {
          setError(err)
        }
      } finally {
        // Only clear loading if this is still the current request
        if (thisRequestId === currentRequestIdRef.current) {
          setLoading(false)
        }
      }
    }

    fetchIds()
  }, [memoizedFilters, user, isAdminModeActive, userDataReady, cacheKey])

  // Load a batch of items from Firestore
  const loadItemsBatch = async (
    leftItems: TattooItemRef[],
    rightItems: TattooItemRef[],
    startIndex: number,
    count: number,
    requestId: number,
    replaceList = false
  ) => {
    if (replaceList) {
      loadedItemIdsRef.current.clear()
    }

    // Calculate how many items to load from each column
    const itemsPerColumn = Math.ceil(count / 2)
    const leftBatch = leftItems.slice(startIndex, startIndex + itemsPerColumn)
    const rightBatch = rightItems.slice(startIndex, startIndex + itemsPerColumn)
    const allItemsToFetch = [...leftBatch, ...rightBatch]

    if (allItemsToFetch.length === 0) return

    // Fetch all documents in parallel - now we know exactly which collection to use
    const itemPromises = allItemsToFetch.map(async (itemRef) => {
      try {
        // Map the type to the correct collection name
        let collectionName = 
          itemRef.type === "flash" ? "flashes" : 
          itemRef.type === "healedPortfolioItem" ? "healedPortfolioItems" : 
          "portfolioItems"
        let docRef = doc(db, collectionName, itemRef.id)
        let docSnap = await getDoc(docRef)
        
        // For flashes, also check archived if not found
        if (!docSnap.exists() && itemRef.type === "flash") {
          collectionName = "flashesArchived"
          docRef = doc(db, collectionName, itemRef.id)
          docSnap = await getDoc(docRef)
        }
        
        // For healed items, mark them as healed in the portfolio item
        if (itemRef.type === "healedPortfolioItem" && docSnap.exists()) {
        }
        
        if (!docSnap.exists()) {
          console.warn(`[useQueryTattoos] missing doc ${itemRef.id} in ${collectionName}`)
          return null
        }

        const data = docSnap.data()

        // Soft-deleted flashes are archived with status artistDeleted. Stale query ID lists
        // can still reference them; the archived fallback above would otherwise resurrect them in the grid.
        if (itemRef.type === "flash" && data.status === "artistDeleted") {
          return null
        }

        const item: PortfolioItem = {
          id: docSnap.id,
          artistId: data.artistId as string,
          type: (data.type as PortfolioItem["type"]) ?? "image",
          storagePath: (data.storagePath as string) ?? "",
          imageUrl: data.imageUrl as string,
          width: (data.width as number) ?? 0,
          height: (data.height as number) ?? 0,
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
        }
        
        // Add optional properties
        if (data.compressedImageUrl) item.compressedImageUrl = data.compressedImageUrl
        if (data.styles) item.styleTags = data.styles as string[]
        if (data.caption) item.caption = data.caption as string
        if (data.timedSaves) {
          item.timedSaves = data.timedSaves.map((saveData: any) => ({
            itemId: saveData.itemId,
            userId: saveData.userId,
            savedAt: saveData.savedAt?.toDate?.() || new Date(saveData.savedAt)
          }))
        }

        // Flash-specific properties
        if (itemRef.type === "flash") {
          item.isFlash = true
          if (data.minPrice) item.minPrice = data.minPrice
          if (data.minSize) item.minSize = data.minSize
          if (data.available !== undefined) item.available = data.available
          if (data.booked !== undefined) item.booked = data.booked
          if (data.accessCode) item.accessCode = data.accessCode
          if (data.eventId) item.eventId = data.eventId
          if (data.eventExclusive !== undefined) item.eventExclusive = data.eventExclusive
          
          // Handle discount field
          if (data.discount) {
            item.discount = {
              absoluteAmount: data.discount.absoluteAmount,
              procentualAccount: data.discount.procentualAccount,
              newPrice: data.discount.newPrice,
              oldPrice: data.discount.oldPrice,
              discountStartsAt: data.discount.discountStartsAt?.toDate?.() || new Date(data.discount.discountStartsAt),
              discountEndsAt: data.discount.discountEndsAt?.toDate?.() || new Date(data.discount.discountEndsAt)
            }
          }
        }
        
        // Healed portfolio-specific properties
        if (itemRef.type === "healedPortfolioItem") {
          item.isHealed = true
        }
        
        return item
      } catch (err) {
        console.error(`[useQueryTattoos] doc fetch error ${itemRef.id}:`, err)
        return null
      }
    })

    const fetchedItems = await Promise.all(itemPromises)
    const validItems = fetchedItems.filter((item): item is PortfolioItem => item !== null)

    // Check if this request is still current before updating state
    if (requestId !== currentRequestIdRef.current) {
      return
    }

    const missingCount = allItemsToFetch.length - validItems.length
    if (missingCount > 0) {
      console.warn(
        `[useQueryTattoos] batch incomplete: ${missingCount}/${allItemsToFetch.length} missing`
      )
    }

    // Create a map to preserve the order from backend (left/right columns)
    const itemMap = new Map<string, PortfolioItem>()
    validItems.forEach(item => itemMap.set(item.id, item))

    // Reconstruct items in the correct order (interleaving left and right)
    // ALSO deduplicate against already-loaded items
    const orderedItems: PortfolioItem[] = []
    const maxLength = Math.max(leftBatch.length, rightBatch.length)
    
    for (let i = 0; i < maxLength; i++) {
      if (i < leftBatch.length && itemMap.has(leftBatch[i].id)) {
        const itemId = leftBatch[i].id
        if (!loadedItemIdsRef.current.has(itemId)) {
          orderedItems.push(itemMap.get(itemId)!)
          loadedItemIdsRef.current.add(itemId)
        }
      }
      if (i < rightBatch.length && itemMap.has(rightBatch[i].id)) {
        const itemId = rightBatch[i].id
        if (!loadedItemIdsRef.current.has(itemId)) {
          orderedItems.push(itemMap.get(itemId)!)
          loadedItemIdsRef.current.add(itemId)
        }
      }
    }

    const deduplicatedCount = validItems.length - orderedItems.length
    if (deduplicatedCount > 0) {
      console.warn(`[useQueryTattoos] deduplicated ${deduplicatedCount} items already loaded`)
    }

    setLoadedItems((prev) => {
      const updatedItems = replaceList ? orderedItems : [...prev, ...orderedItems]
      if (cacheKey) {
        feedCache.setEntry(cacheKey, {
          items: updatedItems,
          allLeftItems: leftItems,
          allRightItems: rightItems,
          loadedItemIds: new Set(loadedItemIdsRef.current),
          currentLoadedCount: replaceList
            ? orderedItems.length
            : loadedItemIdsRef.current.size,
          cachedAt: Date.now(),
          scrollPosition: 0,
        })
      }
      return updatedItems
    })
    if (replaceList) {
      setCurrentLoadedCount(orderedItems.length)
    } else {
      setCurrentLoadedCount((prev) => prev + orderedItems.length)
    }
  }

  // Load more items (for infinite scroll)
  const loadMoreItems = useCallback(async () => {
    if (loadingMore || !filters) return

    const totalAvailableItems = allLeftItems.length + allRightItems.length
    const hasMore = currentLoadedCount < totalAvailableItems

    if (!hasMore) {
      return
    }

    setLoadingMore(true)

    try {
      if (currentRequestIdRef.current === 0) {
        currentRequestIdRef.current = 1
      }
      const currentRequestId = currentRequestIdRef.current
      const traceId = createDiscoverTraceId()
      const loadMoreStartedAt = discoverPerfNow()

      const filterPayload = queryTattoosFiltersForRequest(filters)
      const requestBody = {
        ...filterPayload,
        sorting: sortingForQueryTattoosApi(filterPayload),
        userId: user?.uid || null,
        isAdmin: isAdminModeActive,
        offset: currentLoadedCount,
        limit: ITEMS_PER_LOAD,
      }

      const response = await fetch(QUERY_TATTOOS_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Discover-Trace-Id": traceId,
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        throw new Error(`Backend returned ${response.status}: ${response.statusText}`)
      }

      const data: QueryTattoosResponse = await response.json()
      if (currentRequestId !== currentRequestIdRef.current) {
        return
      }

      if (data.items && data.items.length > 0) {
        const hydratedItems = mapApiItemsToPortfolioItems(data.items, loadedItemIdsRef.current)
        hydratedItems.forEach((item) => loadedItemIdsRef.current.add(item.id))
        setLoadedItems((previousItems) => {
          const updatedItems = [...previousItems, ...hydratedItems]
          if (cacheKey) {
            feedCache.setEntry(cacheKey, {
              items: updatedItems,
              allLeftItems,
              allRightItems,
              loadedItemIds: new Set(loadedItemIdsRef.current),
              currentLoadedCount: updatedItems.length,
              cachedAt: Date.now(),
              scrollPosition: 0,
            })
          }
          return updatedItems
        })
        setCurrentLoadedCount((previousCount) => previousCount + hydratedItems.length)
        finishDiscoverLoadPhase("query_tattoos_load_more_hydrated", loadMoreStartedAt, {
          requestId: currentRequestId,
          traceId,
          itemCount: hydratedItems.length,
          offset: currentLoadedCount,
        })
        return
      }

      const startIndex = Math.floor(currentLoadedCount / 2)
      await loadItemsBatch(allLeftItems, allRightItems, startIndex, ITEMS_PER_LOAD, currentRequestId)
    } catch (err) {
      console.error("[useQueryTattoos] loadMore failed:", err)
      setError(err)
    } finally {
      setLoadingMore(false)
    }
  }, [
    loadingMore,
    allLeftItems,
    allRightItems,
    currentLoadedCount,
    filters,
    user,
    isAdminModeActive,
    cacheKey,
    feedCache,
  ])

  const totalAvailableItems = allLeftItems.length + allRightItems.length
  const hasMore = currentLoadedCount < totalAvailableItems

  const prefetchUntilItemId = options?.prefetchUntilItemId ?? null

  useEffect(() => {
    if (!prefetchUntilItemId || !initialLoadComplete || loading) return
    if (loadingMore) return

    if (loadedItems.some((i) => i.id === prefetchUntilItemId)) return

    const inQueryResults =
      allLeftItems.some((r) => r.id === prefetchUntilItemId) ||
      allRightItems.some((r) => r.id === prefetchUntilItemId)
    if (!inQueryResults) return

    if (currentLoadedCount >= totalAvailableItems) return

    void loadMoreItems()
  }, [
    prefetchUntilItemId,
    initialLoadComplete,
    loading,
    loadingMore,
    loadedItems,
    allLeftItems,
    allRightItems,
    currentLoadedCount,
    totalAvailableItems,
    loadMoreItems,
  ])

  return {
    items: loadedItems,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMoreItems,
    initialLoadComplete,
    searchScores: undefined,
  }
}

